import { Workbox } from 'workbox-window';

interface ServiceWorkerCallbacks {
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onError?: (error: Error) => void;
  onOfflineReady?: () => void;
  onNeedRefresh?: () => void;
}

class ServiceWorkerManager {
  private wb: Workbox | null = null;
  private callbacks: ServiceWorkerCallbacks = {};

  constructor() {
    if ('serviceWorker' in navigator && import.meta.env.PROD) {
      this.wb = new Workbox('/sw.js');
      this.setupEventListeners();
    }
  }

  private setupEventListeners() {
    if (!this.wb) return;

    // Service worker has been installed and is waiting to activate
    this.wb.addEventListener('waiting', () => {
      console.log('Service worker is waiting to activate');
      this.callbacks.onNeedRefresh?.();
    });

    // Service worker has been activated and is controlling the page
    this.wb.addEventListener('controlling', () => {
      console.log('Service worker is now controlling the page');
      window.location.reload();
    });

    // Service worker has been installed for the first time
    this.wb.addEventListener('installed', (event) => {
      console.log('Service worker installed');
      if (event.isUpdate) {
        this.callbacks.onUpdate?.(event.sw as any);
      } else {
        this.callbacks.onSuccess?.(event.sw as any);
        this.callbacks.onOfflineReady?.();
      }
    });

    // Service worker registration failed
    this.wb.addEventListener('redundant', () => {
      console.log('Service worker became redundant');
    });

    // Handle service worker errors
    this.wb.addEventListener('message', (event) => {
      if (event.data && event.data.type === 'ERROR') {
        console.error('Service worker error:', event.data.error);
        this.callbacks.onError?.(new Error(event.data.error));
      }
    });
  }

  public register(callbacks: ServiceWorkerCallbacks = {}) {
    this.callbacks = callbacks;

    if (!this.wb) {
      console.log('Service worker not supported or not in production mode');
      return Promise.resolve();
    }

    return this.wb.register()
      .then((registration) => {
        console.log('Service worker registered successfully');
        if (registration) {
          this.callbacks.onSuccess?.(registration);
        }
        return registration;
      })
      .catch((error) => {
        console.error('Service worker registration failed:', error);
        this.callbacks.onError?.(error);
        throw error;
      });
  }

  public update() {
    if (!this.wb) return;

    // Skip waiting and activate the new service worker
    this.wb.messageSkipWaiting();
  }

  public unregister() {
    if ('serviceWorker' in navigator) {
      return navigator.serviceWorker.ready
        .then((registration) => {
          return registration.unregister();
        })
        .catch((error) => {
          console.error('Service worker unregistration failed:', error);
        });
    }
    return Promise.resolve();
  }

  // Check if the app is running offline
  public isOffline(): boolean {
    return !navigator.onLine;
  }

  // Get cached data when offline
  public async getCachedData(url: string): Promise<Response | null> {
    if ('caches' in window) {
      try {
        const cache = await caches.open('api-cache');
        const response = await cache.match(url);
        return response || null;
      } catch (error) {
        console.error('Error accessing cache:', error);
        return null;
      }
    }
    return null;
  }

  // Preload important routes for offline access
  public async preloadRoutes(routes: string[]) {
    if ('caches' in window) {
      try {
        const cache = await caches.open('pages-cache');
        await cache.addAll(routes);
        console.log('Routes preloaded for offline access');
      } catch (error) {
        console.error('Error preloading routes:', error);
      }
    }
  }

  // Clear all caches (useful for debugging)
  public async clearCaches() {
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
        console.log('All caches cleared');
      } catch (error) {
        console.error('Error clearing caches:', error);
      }
    }
  }
}

// Singleton instance
export const serviceWorkerManager = new ServiceWorkerManager();

// Hook for React components to interact with service worker
export const useServiceWorker = () => {
  const [isOffline, setIsOffline] = React.useState(!navigator.onLine);
  const [needRefresh, setNeedRefresh] = React.useState(false);
  const [offlineReady, setOfflineReady] = React.useState(false);

  React.useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Register service worker with callbacks
    serviceWorkerManager.register({
      onNeedRefresh: () => setNeedRefresh(true),
      onOfflineReady: () => setOfflineReady(true),
      onError: (error) => {
        console.error('Service worker error:', error);
      },
    });

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const updateServiceWorker = () => {
    serviceWorkerManager.update();
    setNeedRefresh(false);
  };

  return {
    isOffline,
    needRefresh,
    offlineReady,
    updateServiceWorker,
  };
};

// React import for the hook
import React from 'react';